/**
 * MyWmsOrders — "Your open Social Pro orders" with the script journey for each one.
 * Live from the agent's own WMS login, or via their branch when they arrived through the WMS button.
 */
import { useEffect, useState } from 'react';
import axios from 'axios';
import { ArrowRight, RefreshCw, Loader2, ScrollText, ChevronDown, AlertCircle, Clapperboard, PackageCheck, Send } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export const STAGES = [
  { key: 'needs_script', label: 'Needs script', hint: 'Write the script so the editors can start' },
  { key: 'script_sent', label: 'Script sent', hint: 'With the editing team — waiting to be picked up' },
  { key: 'in_edit', label: 'In edit', hint: 'An editor is cutting your video now' },
  { key: 'delivered', label: 'Delivered', hint: 'Finished — find it in your Social Library' },
];
const stageIndex = (s) => Math.max(0, STAGES.findIndex((x) => x.key === s));

const fmtDate = (s) => {
  if (!s) return '';
  const d = new Date(String(s).replace(' ', 'T'));
  return Number.isNaN(d.getTime()) ? s : d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
};

export const StageTracker = ({ stage, orderId }) => {
  const idx = stageIndex(stage);
  return (
    <ol className="wms-stage" data-testid={`wms-order-stage-${orderId}`} data-stage={stage || 'needs_script'} aria-label={`Stage: ${STAGES[idx].label}`}>
      {STAGES.map((s, i) => (
        <li key={s.key} className={`wms-stage-step ${i < idx ? 'is-done' : ''} ${i === idx ? 'is-current' : ''}`} title={s.hint}>
          <span className="wms-stage-dot" />
          <span className="wms-stage-label">{s.label}</span>
        </li>
      ))}
    </ol>
  );
};

const StageAction = ({ o, onScript, onLibrary }) => {
  const stage = o.stage || (o.script_order_id ? 'script_sent' : 'needs_script');
  if (stage === 'needs_script' && o.eligible) {
    return <button type="button" className="wms-order-cta" onClick={() => onScript(o)} data-testid={`wms-order-script-${o.order_id}`}><ScrollText size={13} /> Write the script <ArrowRight size={13} /></button>;
  }
  if (stage === 'script_sent') {
    return <span className="wms-order-badge is-done" data-testid={`wms-order-scripted-${o.order_id}`}><Send size={13} /> Script sent · awaiting editor</span>;
  }
  if (stage === 'in_edit') {
    return <span className="wms-order-badge is-edit" data-testid={`wms-order-inedit-${o.order_id}`}><Clapperboard size={13} /> In edit{o.avatar_name ? ` · ${o.avatar_name}` : ''}</span>;
  }
  if (stage === 'delivered') {
    return <button type="button" className="wms-order-badge is-delivered as-button" onClick={() => onLibrary(o)} data-testid={`wms-order-delivered-${o.order_id}`}><PackageCheck size={13} /> Delivered · open library <ArrowRight size={12} /></button>;
  }
  return <span className="wms-order-badge">{o.progress || 'Active'}</span>;
};

const OrderCard = ({ o, onScript, onLibrary }) => (
  <li className={`wms-order ${o.eligible ? 'is-social' : ''}`} data-testid={`wms-order-${o.order_id}`}>
    <div className="wms-order-main">
      <span className="wms-order-address" data-testid={`wms-order-address-${o.order_id}`}>{o.address || `Order #${o.order_id}`}</span>
      <span className="wms-order-meta">#{o.order_id} · {o.branch_name || o.client_name} · booked {fmtDate(o.created_at)}</span>
      <span className="wms-order-products">
        {o.products.slice(0, 4).map((p) => <em key={p} className={o.matched_product === p ? 'is-match' : ''}>{p}</em>)}
        {o.products.length > 4 && <em>+{o.products.length - 4}</em>}
      </span>
    </div>
    {o.eligible && <StageTracker stage={o.stage} orderId={o.order_id} />}
    <div className="wms-order-side">
      <StageAction o={o} onScript={onScript} onLibrary={onLibrary} />
    </div>
  </li>
);

export default function MyWmsOrders({ onSelectModule }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [showOther, setShowOther] = useState(false);

  const load = (refresh = false) => {
    setBusy(true);
    axios.get(`${API}/api/wms/my-orders${refresh ? '?refresh=1' : ''}`)
      .then((r) => setData(r.data))
      .catch(() => setData({ linked: false }))
      .finally(() => setBusy(false));
  };
  useEffect(() => { load(false); }, []);

  if (!data || (!data.linked && !data.branch_fallback) || data.placeholder) return null;
  const social = data.social_pro || [];
  const other = data.other || [];
  const toScript = (o) => {
    try { sessionStorage.setItem('nc-preselect-wms-order', String(o.order_id)); } catch { /* private mode */ }
    onSelectModule('script');
  };
  const toLibrary = (o) => {
    if (o?.order_id) { try { sessionStorage.setItem('nc-open-asset-order', String(o.order_id)); } catch { /* private mode */ } }
    onSelectModule('social-hub');
  };
  const counts = STAGES.map((s) => ({ ...s, n: social.filter((o) => (o.stage || 'needs_script') === s.key).length }));

  return (
    <section className="wms-orders" data-testid="wms-my-orders" data-source={data.source || (data.linked ? 'wms-user' : 'branch')}>
      <header className="wms-orders-head">
        <div>
          <span className="wms-orders-eyebrow" data-testid="wms-orders-source">{data.linked ? 'Live from WMS' : 'Via your branch · WMS'}</span>
          <h2 className="wms-orders-title">
            {social.length > 0 ? `${social.length} open Social Pro ${social.length === 1 ? 'order' : 'orders'}` : 'Your open Social Pro orders'}
          </h2>
        </div>
        {social.length > 0 && (
          <ul className="wms-orders-summary" data-testid="wms-orders-summary">
            {counts.map((c) => <li key={c.key} className={c.n ? 'has' : ''} data-testid={`wms-summary-${c.key}`}><strong>{c.n}</strong> {c.label}</li>)}
          </ul>
        )}
        <button type="button" className="wms-orders-refresh" onClick={() => load(true)} disabled={busy} aria-label="Refresh from WMS" data-testid="wms-orders-refresh">
          {busy ? <Loader2 size={15} className="spin" /> : <RefreshCw size={15} />}
        </button>
      </header>

      {data.needs_relogin && (
        <p className="wms-orders-note is-warn" data-testid="wms-orders-relogin"><AlertCircle size={14} /> WMS has signed you out — log out and back in with your WMS login to refresh your orders.</p>
      )}
      {data.wms_down && (
        <p className="wms-orders-note is-warn" data-testid="wms-orders-down"><AlertCircle size={14} /> WMS isn't answering right now — we'll show your orders as soon as it's back.</p>
      )}

      {social.length > 0 ? (
        <ul className="wms-orders-list" data-testid="wms-orders-social">
          {social.map((o) => <OrderCard key={o.order_id} o={o} onScript={toScript} onLibrary={toLibrary} />)}
        </ul>
      ) : (!data.needs_relogin && !data.wms_down && (
        <p className="wms-orders-note" data-testid="wms-orders-empty">
          No Social Pro orders open right now{data.total_open > 0 ? ` — ${data.total_open} other open ${data.total_open === 1 ? 'order' : 'orders'} on WMS.` : '.'}
        </p>
      ))}

      {other.length > 0 && (
        <div className="wms-orders-other">
          <button type="button" className="wms-orders-toggle" onClick={() => setShowOther((v) => !v)} data-testid="wms-orders-toggle-other">
            <ChevronDown size={14} className={showOther ? 'is-open' : ''} /> {showOther ? 'Hide' : 'Show'} other open orders ({other.length}{data.total_open > other.length ? ` of ${data.total_open}` : ''})
          </button>
          {showOther && (
            <ul className="wms-orders-list is-muted" data-testid="wms-orders-other">
              {other.map((o) => <OrderCard key={o.order_id} o={o} onScript={toScript} onLibrary={toLibrary} />)}
            </ul>
          )}
        </div>
      )}
    </section>
  );
}
